/**
 * The sidebar navigation tree.
 *
 * Three levels — group, collection, item — told apart by indentation and weight
 * rather than by rules, boxes or colour. Deliberately absent: counts, badges,
 * "3 of 5", "2 collections". A number beside a menu entry is metadata about the
 * menu, and nobody navigates by it.
 *
 * Groups are permanent sections, not accordions. They have no chevron and no
 * toggle: a heading that can hide its own contents makes the sidebar's shape
 * change under the user, and the shape is the thing that makes twenty entries
 * scannable. Only a collection that actually has children can fold.
 *
 * Nothing here reorders anything. Customising the arrangement lives on its own
 * screen, reached from the profile menu, so everyday navigation carries no
 * handles, no edit affordances and no settings button.
 */
import { useCallback, useMemo, useState } from 'react';
import type { NavCollection, NavGroup, NavItem } from './nav-model';
import { Icon, piIconName } from '../common/icon';

const EXPANDED_KEY = 'nav.expanded.v1';

const expandedKey = (userId: string) => `${EXPANDED_KEY}:${userId || 'anon'}`;

const readExpanded = (userId: string): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(expandedKey(userId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

interface SideNavProps {
  nav: NavGroup[];
  /** Pinned above the groups; never part of the arrangement. */
  dashboard: NavCollection;
  /** `location.pathname + location.search`, so `?tab=` marks the right item. */
  activePath: string;
  onNavigate: (route: string) => void;
  /** Scopes the remembered fold state, so it does not leak between accounts. */
  userId: string;
  /** Hidden while the rail is collapsed to icons. */
  collapsed?: boolean;
  testId?: string;
}

const SideNav = ({
  nav,
  dashboard,
  activePath,
  onNavigate,
  userId,
  collapsed = false,
  testId,
}: SideNavProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => readExpanded(userId));
  const [search, setSearch] = useState('');

  const isActive = useCallback(
    (route: string) => {
      // A collection is active for any route beneath it, so /units/:id/edit
      // still marks Units. Compare on the pathname only: `?tab=` decides which
      // CHILD is active, not whether the parent is.
      const base = route.split('?')[0];
      const here = activePath.split('?')[0];
      return here === base || here.startsWith(`${base}/`);
    },
    [activePath]
  );

  /** An item is active only on an exact match, `?tab=` included. */
  const isItemActive = useCallback(
    (route: string) => {
      const [base, q] = route.split('?');
      const [here, hq] = activePath.split('?');
      if (here !== base) return false;
      const wanted = new URLSearchParams(q || '');
      const here_q = new URLSearchParams(hq || '');
      const want = wanted.get('tab');
      // No `?tab=` on the child route means it is its own page, not one tab
      // among siblings — a pathname match is enough.
      if (!want) return true;
      if (here_q.get('tab') !== want) return false;
      // `view` is the sub-tab inside a grouped tab. Two entries can now share a
      // tab — Notification Types and Notification Master both live under
      // `?tab=notifications` — so without this both would light up at once.
      // Only checked when the route names one, so every other entry is unaffected.
      const wantView = wanted.get('view');
      if (!wantView) return true;
      // Compared strictly. Falling back to "the panel probably defaulted to this
      // one" would light up every entry sharing the tab, which is the exact
      // problem this check exists to prevent. Landing on `?tab=notifications`
      // with no view highlights the collection but no child — honest, and better
      // than highlighting two.
      return here_q.get('view') === wantView;
    },
    [activePath]
  );

  const toggle = useCallback(
    (id: string, fallback: boolean) => {
      setExpanded((prev) => {
        const next = { ...prev, [id]: !(prev[id] ?? fallback) };
        try {
          localStorage.setItem(expandedKey(userId), JSON.stringify(next));
        } catch {
          /* a lost preference is not worth a failed click */
        }
        return next;
      });
    },
    [userId]
  );

  /**
   * A collection holding the route you are on opens itself — but only as the
   * DEFAULT. The saved preference is read first, so one you deliberately folded
   * stays folded even while you are inside it. Auto-opening over an explicit
   * choice is the bug where a tree keeps springing back open.
   */
  const opensByDefault = useCallback(
    (c: NavCollection) => c.children.some((i) => isItemActive(i.route)) || isActive(c.route),
    [isActive, isItemActive]
  );

  /**
   * Search, kept from the flat menu it replaced, matching on every level.
   *
   * A hit on a collection keeps the whole collection; a hit only on a child
   * keeps the collection with just the matching children, so you can see what
   * matched and where it lives. Groups left with nothing drop out.
   */
  const tree = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return nav;
    return nav
      .map((g) => ({
        ...g,
        collections: g.collections
          .map((c) => {
            if (c.label.toLowerCase().includes(q)) return c;
            const kids = c.children.filter((i) => i.label.toLowerCase().includes(q));
            return kids.length ? { ...c, children: kids } : null;
          })
          .filter((c): c is NavCollection => c !== null),
      }))
      .filter((g) => g.collections.length > 0);
  }, [nav, search]);

  const searching = search.trim().length > 0;

  if (collapsed) {
    // Icons only. The tree needs width to say anything, so the rail shows the
    // collections as a strip of icons rather than a squeezed hierarchy.
    return (
      <nav className="flex-1 overflow-y-auto py-2" aria-label="Main" data-testid={testId}>
        {[dashboard, ...nav.flatMap((g) => g.collections)].map((c) => (
          <button
            key={c.id}
            type="button"
            title={c.label}
            aria-label={c.label}
            aria-current={isActive(c.route) ? 'page' : undefined}
            onClick={() => onNavigate(c.route)}
            className={`w-full flex justify-center py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 ${
              isActive(c.route)
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {piIconName(c.icon)
              ? <Icon name={piIconName(c.icon)!} size={19} />
              : <i className={`${c.icon} text-base`} />}
          </button>
        ))}
      </nav>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid={testId}>
      <div className="px-3 pt-2.5 pb-2">
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"><Icon name="search" size={13} /></span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search menu..."
            aria-label="Search menu"
            className="w-full rounded-md border border-line bg-transparent py-1.5 pl-7 pr-2 text-[15px] text-gray-700 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/40 transition-colors"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-3 pt-0.5" aria-label="Main">
        {searching && tree.length === 0 && (
          <p className="px-2 py-3 text-[15px] text-gray-400">No matches</p>
        )}

        {/* Dashboard sits outside the groups: it is where the app opens, so it
            is not something to fold away or rearrange. */}
        {!searching && (
          <button
            type="button"
            aria-current={isActive(dashboard.route) ? 'page' : undefined}
            onClick={() => onNavigate(dashboard.route)}
            className={`relative w-full flex items-center gap-2.5 rounded-lg px-2 py-[7px] mb-1.5 text-[15px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
              isActive(dashboard.route)
                ? 'bg-indigo-50 dark:bg-indigo-400/15 text-indigo-700 dark:text-indigo-200'
                : 'text-gray-800 dark:text-gray-100 hover:bg-gray-100/80 dark:hover:bg-gray-700/40'
            }`}
          >
            {isActive(dashboard.route) && (
              <span
                aria-hidden="true"
                className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-brand-gradient"
              />
            )}
            {piIconName(dashboard.icon)
              ? <Icon name={piIconName(dashboard.icon)!} size={17} className={`shrink-0 ${isActive(dashboard.route) ? 'text-indigo-600 dark:text-indigo-300' : 'text-ink-muted'}`} />
              : <i className={`${dashboard.icon} text-sm w-4 text-center ${isActive(dashboard.route) ? 'text-indigo-600 dark:text-indigo-300' : 'text-ink-muted'}`} />}
            <span className="truncate">{dashboard.label}</span>
          </button>
        )}

        {tree.map((g) => (
          /* A permanent section, not an accordion: a heading with no control on
             it, and its collections always underneath. */
          <section key={g.id} className="mt-3 first:mt-0" aria-labelledby={`navgrp-${g.id}`}>
            {/* Heading only — no rule, no chevron, no control of any kind. The
                separation between groups is the `mt-3` above and the weight of
                this line; a divider would add a horizontal stroke to a surface
                whose whole job is a vertical list. */}
            <h3
              id={`navgrp-${g.id}`}
              className="px-2 pt-1 pb-0.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-gray-800 dark:text-gray-100 truncate"
              title={g.label}
            >
              {g.label}
            </h3>

            <ul>
              {g.collections.map((c) => {
                const hasKids = c.children.length > 0;
                const cOpen = hasKids && (searching || (expanded[c.id] ?? opensByDefault(c)));
                /* Two different things, which used to be one.
                   `active` is "you are somewhere in this section" — true for any
                   route beneath the collection. `selfSelected` is "this row IS
                   the page". When a child is the destination, both the parent and
                   the child were drawn with the full selected treatment — the
                   background, the indigo, and the rail bar — so two rows claimed
                   to be the current page and neither read as the answer.
                   The parent now keeps only the indigo text, as a trail showing
                   where you are; the strong treatment belongs to the one row you
                   actually opened. */
                const childOn = hasKids && c.children.some((i) => isItemActive(i.route));
                const active = isActive(c.route);
                const selfSelected = active && !childOn;
                return (
                  <li key={c.id}>
                    <div
                      className={`relative flex items-center rounded-md transition-colors duration-150 ${
                        selfSelected
                          ? 'bg-indigo-50 dark:bg-indigo-400/10'
                          : 'hover:bg-gray-100/80 dark:hover:bg-gray-700/40'
                      }`}
                    >
                      {/* The marker for where you are: a short rounded bar on
                          the rail rather than a full border, so it reads as a
                          highlight and not as a table rule. */}
                      {selfSelected && (
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-brand-gradient"
                        />
                      )}
                      {/* The chevron is its own control so opening a collection
                          and navigating to it stay separate actions. */}
                      {hasKids ? (
                        <button
                          type="button"
                          aria-expanded={cOpen}
                          aria-controls={`navcol-${c.id}`}
                          aria-label={`${cOpen ? 'Collapse' : 'Expand'} ${c.label}`}
                          onClick={() => toggle(c.id, opensByDefault(c))}
                          className="shrink-0 pl-1.5 pr-0.5 py-[5px] rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        >
                          <Icon
                            name="chevron-right"
                            size={13}
                            className={`text-gray-400 transition-transform duration-200 ${cOpen ? 'rotate-90' : ''}`}
                          />
                        </button>
                      ) : (
                        <span className="shrink-0 pl-1.5 w-[15px]" aria-hidden="true" />
                      )}
                      <button
                        type="button"
                        // Only the row that IS the page may claim it: two
                        // elements with aria-current="page" is a screen reader
                        // announcing the same ambiguity the colours did.
                        aria-current={selfSelected ? 'page' : undefined}
                        onClick={() => onNavigate(c.route)}
                        title={c.label}
                        className={`flex-1 min-w-0 flex items-center gap-2.5 px-1.5 py-[5px] text-[15px] text-left rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                          selfSelected
                            ? 'text-indigo-700 dark:text-indigo-200 font-medium'
                            : active
                              ? 'text-indigo-700 dark:text-indigo-200'
                              : 'text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {piIconName(c.icon)
                          ? <Icon name={piIconName(c.icon)!} size={17} className={`shrink-0 transition-colors ${active ? 'text-indigo-600 dark:text-indigo-300' : 'text-ink-muted'}`} />
                          : <i className={`${c.icon} text-sm w-4 text-center shrink-0 transition-colors ${active ? 'text-indigo-600 dark:text-indigo-300' : 'text-ink-muted'}`} />}
                        <span className="truncate">{c.label}</span>
                      </button>
                    </div>

                    {hasKids && (
                      /* grid-template-rows animates to and from `auto` without a
                         measured height, so a collection of any size opens
                         smoothly and needs no JS to size it. */
                      <div
                        id={`navcol-${c.id}`}
                        className="grid transition-all duration-200 ease-out"
                        style={{ gridTemplateRows: cOpen ? '1fr' : '0fr', opacity: cOpen ? 1 : 0 }}
                      >
                        <ul className="overflow-hidden ml-[19px] pl-2.5 border-l border-gray-200 dark:border-gray-700/50">
                          {c.children.map((it: NavItem) => {
                            const on = isItemActive(it.route);
                            return (
                              <li key={it.id}>
                                <button
                                  type="button"
                                  aria-current={on ? 'page' : undefined}
                                  onClick={() => onNavigate(it.route)}
                                  title={it.label}
                                  className={`w-full text-left rounded-md pl-2.5 pr-2 py-[4px] text-[14.5px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                                    on
                                      ? 'bg-indigo-50 dark:bg-indigo-400/10 text-indigo-700 dark:text-indigo-200 font-medium'
                                      : 'text-ink-muted hover:bg-gray-100/80 dark:hover:bg-gray-700/40 hover:text-gray-800 dark:hover:text-gray-200'
                                  }`}
                                >
                                  <span className="block truncate">{it.label}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>
    </div>
  );
};

export default SideNav;
